/**
 * Centralized Date Formatting Utilities
 * 
 * Shared utilities for consistent date formatting across all dashboard cards
 * including tooltips, axes labels, and legends.
 */

import dayjs from 'dayjs';

export type AggregationType = 'daily' | 'weekly' | 'monthly';

/**
 * Format X-axis labels based on aggregation type
 * Used for chart X-axis display
 */
export const formatXAxisLabel = (value: string, aggregation: AggregationType): string => {
  if (aggregation === 'daily') {
    return dayjs(value).format('MMM DD');
  } else if (aggregation === 'weekly') {
    // Weekly IDs like "2025-W03"
    const match = value.match(/(\d{4})-W(\d+)/);
    if (match) {
      return `W${match[2]}`;
    }
    return dayjs(value).format('MMM DD');
  } else {
    return dayjs(value).format('MMM YY');
  }
};

/**
 * Format tooltip labels based on aggregation type
 * Used for chart tooltips and hover information
 */
export const formatTooltipLabel = (value: string, aggregation: AggregationType): string => {
  if (aggregation === 'daily') {
    return dayjs(value).format('MMM DD, YYYY');
  } else if (aggregation === 'weekly') {
    const match = value.match(/(\d{4})-W(\d+)/);
    if (match) {
      return `Week ${match[2]}, ${match[1]}`;
    }
    return dayjs(value).format('MMM DD, YYYY');
  } else {
    return dayjs(value).format('MMMM YYYY');
  }
};

/**
 * Format period display for summary panels
 * Used in dashboard card summary statistics
 */
export const formatPeriodDisplay = (dataLength: number, aggregation: AggregationType): string => {
  const periodUnit = aggregation === 'daily' ? 'day' : 
                    aggregation === 'weekly' ? 'week' : 'month';
  const plural = dataLength !== 1 ? 's' : '';
  return `${dataLength} ${periodUnit}${plural}`;
};

/**
 * Calculate smart interval for X-axis labels
 * Optimizes label density based on data point count
 */
export const getXAxisInterval = (pointCount: number): number => {
  if (pointCount <= 12) return 0;  // Show all labels
  if (pointCount <= 30) return 1;  // Every 2nd label
  if (pointCount <= 60) return 4;  // Every 5th label
  return Math.floor(pointCount / 10);  // ~10 labels total
};

/**
 * Format date range for display
 * Used in headers and descriptions
 */
export const formatDateRange = (fromDate: string, toDate: string, aggregation: AggregationType): string => {
  const from = dayjs(fromDate);
  const to = dayjs(toDate);
  
  if (aggregation === 'daily') {
    return `${from.format('MMM DD')} - ${to.format('MMM DD, YYYY')}`;
  } else if (aggregation === 'weekly') {
    return `${from.format('MMM DD')} - ${to.format('MMM DD, YYYY')}`;
  } else {
    return `${from.format('MMM YYYY')} - ${to.format('MMM YYYY')}`;
  }
};

/**
 * Format timestamp for display
 * Used for showing specific timestamps in tooltips
 */
export const formatTimestamp = (timestamp: string): string => {
  return dayjs(timestamp).format('MMM DD, YYYY HH:mm');
};

/**
 * Format relative time
 * Used for "time ago" displays
 */
export const formatRelativeTime = (timestamp: string): string => {
  return dayjs(timestamp).fromNow();
};
